import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import { Flip } from 'gsap/Flip';
import { Observer } from 'gsap/Observer';
import { categoryByLegacyKey, categoryNavigation } from '../config/navigation.js';

export function initHomepage(router) {
  if (window.__homepageInitialized) return;
  window.__homepageInitialized = true;
  'use strict';

  let destroyed = false;
  let motionContext = null;

  const $ = (selector, context = document) => context.querySelector(selector);
  const $$ = (selector, context = document) => [...context.querySelectorAll(selector)];
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

  // These sections render plain <a href> markup imperatively (see below), so
  // clicks on internal links here would otherwise trigger a full page reload
  // instead of client-side navigation. Route them through the SPA router.
  const interceptInternalLink = event => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a[href]');
    if (!link) return;
    const href = link.getAttribute('href');
    if (!href || !href.startsWith('/') || href.startsWith('//')) return;
    event.preventDefault();
    router?.push(href);
  };
  $('#categoryPreview')?.addEventListener('click', interceptInternalLink);
  $('#tabView')?.addEventListener('click', interceptInternalLink);
  $('#centerInfo')?.addEventListener('click', interceptInternalLink);

  const revealTargets = $$('.reveal, .reveal-stagger');
  if (!window.gsap && !reducedMotion && 'IntersectionObserver' in window) {
    const revealObserver = new IntersectionObserver(entries => entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      revealObserver.unobserve(entry.target);
    }), { threshold: .12, rootMargin: '0px 0px -40px' });
    revealTargets.forEach(target => revealObserver.observe(target));
  } else revealTargets.forEach(target => target.classList.add('is-visible'));

  const sections = $$('main section[id]');
  if ('IntersectionObserver' in window) {
    const navigationObserver = new IntersectionObserver(entries => entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      $$('.main-nav a').forEach(link => link.classList.toggle('active', link.hash === `#${entry.target.id}`));
    }), { rootMargin: '-35% 0px -58%' });
    sections.forEach(section => navigationObserver.observe(section));
  }

  $$('[data-carousel]').forEach(carousel => {
    const track = $('.resource-track', carousel);
    const step = () => Math.min(track.clientWidth * .76, 310);
    $('.next', carousel)?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    $('.prev', carousel)?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    let startX = 0;
    let startScroll = 0;
    track.addEventListener('pointerdown', event => {
      startX = event.clientX;
      startScroll = track.scrollLeft;
      track.setPointerCapture(event.pointerId);
    });
    track.addEventListener('pointermove', event => {
      if (!track.hasPointerCapture(event.pointerId)) return;
      track.scrollLeft = startScroll - (event.clientX - startX);
    });
  });

  $('#heroSearch')?.addEventListener('submit', event => {
    event.preventDefault();
    const query = $('#heroQuery').value.trim();
    const categoryLabel = $('#heroCategory').value;
    const normalized = value => value.replace('السنّة','السنة');
    const category = categoryNavigation.find(item => normalized(item.label) === normalized(categoryLabel));
    router?.push({ name:'search', query:{ ...(query && {q:query}), ...(category && {section:category.id}), page:'1' } });
  });
  $$('.hero-search-chips [data-quick-category]').forEach(chip => chip.addEventListener('click', () => {
    $('#heroCategory').value = chip.dataset.quickCategory;
    $$('.hero-search-chips [data-quick-category]').forEach(other => other.classList.toggle('active', other === chip));
    $('#heroQuery').focus();
  }));

  const categoryData = {
    sites:{number:'01',title:'مواقع خدمة السنة',description:'مواقع متخصصة تقدم محتوى موثوقًا في خدمة السنة النبوية وعلومها.',icon:'bi-globe2',visual:'sites',resources:[['assets/img/512x512bb4.jpg','موسوعة الحديث (شبكة إسلام ويب)'],['assets/img/512x512bb-112.jpg','الموسوعة الشاملة'],['assets/img/icon10.png','موقع حديث']]},
    apps:{number:'02',title:'التطبيقات الهاتفية',description:'تطبيقات موثوقة تجعل الوصول إلى الحديث وعلومه قريبًا وسهلًا.',icon:'bi-phone',visual:'apps',resources:[['assets/img/512x512bb-112.jpg','تطبيق الموسوعة الحديثية'],['assets/img/download-83231.jpg','جامع الكتب التسعة'],['assets/img/app-image-big-43027.jpg','الإمام المحدث الألباني']]},
    software:{number:'03',title:'خدمات وبرامج حاسوبية',description:'برامج وأدوات حاسوبية تعين الباحث والمتعلم على دراسة السنة.',icon:'bi-laptop',visual:'software',resources:[['assets/img/hadith498.png','جامع الحديث'],['assets/img/unnamed9.jpg','مكتبة الحديث'],['assets/img/512x512bb4.jpg','البواحث الحديثية']]},
    products:{number:'04',title:'منتجات لخدمة السنة',description:'منتجات معرفية وتقنية مختارة ضمن مشاريع خدمة السنة النبوية.',icon:'bi-box-seam',visual:'products',resources:[]},
    media:{number:'05',title:'إصدارات مرئية وصوتية',description:'مواد مرئية وصوتية موثوقة تقرّب علوم السنة لمختلف الجمهور.',icon:'bi-play-btn',visual:'media',resources:[]},
    channels:{number:'06',title:'قنوات وإذاعات',description:'قنوات وإذاعات متخصصة في نشر الحديث والسيرة وعلوم السنة.',icon:'bi-broadcast',visual:'channels',resources:[]},
    research:{number:'07',title:'بحوث في السنة',description:'دراسات وبحوث علمية تساعد الباحثين على الوصول إلى المعرفة الموثوقة.',icon:'bi-file-earmark-text',visual:'research',resources:[['assets/img/hadith498.png','بحوث في السنة النبوية'],['assets/img/unnamed9.jpg','دراسات علوم الحديث'],['assets/img/512x512bb4.jpg','المكتبة البحثية']]},
    centers:{number:'08',title:'مراكز تعليم السنة',description:'دليل بالمراكز والهيئات التي تقدم برامج ومجالس لتعليم السنة.',icon:'bi-mortarboard',visual:'centers',resources:[['assets/img/logo.png','مجالس سماع الحديث بالجامع الأزهر'],['assets/img/logo.png','مؤسسة الشارقة للقرآن والسنة'],['assets/img/logo.png','الإدارة العامة لخدمة الكتاب والسنة']]},
    kids:{number:'09',title:'خدمات السنة للأطفال',description:'مواد وتطبيقات تعليمية تناسب الأجيال الأصغر وتقرّب إليهم الهدي النبوي.',icon:'bi-people',visual:'kids',resources:[]},
    competitions:{number:'10',title:'مسابقات السنة النبوية',description:'مسابقات ومبادرات معرفية تشجع على تعلم السنة وحفظها.',icon:'bi-trophy',visual:'competitions',resources:[]},
    exhibitions:{number:'11',title:'معارض للتعريف بالنبي ﷺ',description:'معارض ومبادرات تقدم السيرة والهدي النبوي بأساليب تعريفية معاصرة.',icon:'bi-easel2',visual:'exhibitions',resources:[]}
  };
  const categoryVisualMarkup = data => {
    if (data.visual === 'apps') return `<div class="preview-apps"><img src="assets/img/512x512bb-112.jpg" alt="تطبيق الموسوعة الحديثية"><img src="assets/img/jamaa-alhdyth-alnboy-18.png" alt="تطبيق جامع الحديث النبوي"></div>`;
    if (data.visual === 'software') return `<div class="preview-software"><div class="category-software-window"><header><span><i></i><i></i><i></i></span><b>الباحث الحديثي</b></header><div class="category-software-search"><i class="bi bi-search"></i><span>ابحث في كتب السنة ومصادرها</span></div><ul><li><i class="bi bi-check2-circle"></i><span><b>حديث صحيح</b><small>موثق من المصدر</small></span></li><li><i class="bi bi-journal-text"></i><span><b>شرح الحديث</b><small>عرض مبسّط ومنظم</small></span></li></ul></div><div class="category-software-stat"><strong>12</strong><span>نتيجة موثقة</span></div></div>`;
    if (data.visual === 'products') return `<div class="preview-product"><img src="assets/img/download-83231.jpg" alt="منتج معرفي لخدمة السنة"><div><span>منتج معرفي</span><strong>جامع الكتب التسعة</strong><small>مصدر رقمي موثوق</small></div></div>`;
    if (data.visual === 'media') return `<div class="preview-media"><div class="media-poster"><i class="bi bi-play-fill"></i><strong>إصدارات السنة</strong></div><div class="media-wave">${'<i></i>'.repeat(18)}</div><small>مرئي وصوتي</small></div>`;
    if (data.visual === 'channels') return `<div class="preview-radio"><span class="radio-logo"><i class="bi bi-broadcast"></i></span><small>يُبث الآن</small><strong>قناة السنة النبوية</strong><div class="radio-track"><i></i></div><div class="radio-controls"><i class="bi bi-skip-backward-fill"></i><i class="bi bi-play-circle-fill"></i><i class="bi bi-skip-forward-fill"></i></div></div>`;
    if (data.visual === 'research') return `<div class="preview-paper"><b>بحث ومراجعة</b><span></span><span></span><span></span><span></span><small>ملاحظة علمية</small><i class="bi bi-search"></i><em></em></div>`;
    if (data.visual === 'centers') return `<div class="preview-mini-map"><svg viewBox="0 0 560 520" aria-hidden="true"><path class="map-water" d="M-20 380C90 310 125 330 210 275s170-12 236-90 105-63 140-98v453H-20Z"/><path class="map-road major" d="M-25 100C92 145 137 232 258 228s189-83 330-21"/><path class="map-road" d="M75-20c12 116 76 175 62 278S58 409 83 555"/><path class="map-road" d="M310-20c-42 94-30 173 18 235s55 157 10 330"/><path class="map-road" d="M-30 325c130-22 210 41 315 23s158-87 305-71"/><path class="map-road minor" d="M15 198c91 8 153-36 232-69s183-18 305 42"/><path class="map-road minor" d="M205-10c16 72-19 118-61 165S85 264 99 340"/></svg><div class="map-location-search"><i class="bi bi-search"></i><span>ابحث عن مركز قريب</span></div><i class="map-dot d1"></i><i class="map-dot d2"></i><i class="map-dot d3"></i><i class="map-dot d4"></i><div class="category-map-card"><i class="bi bi-geo-alt-fill"></i><span>المركز المحدد</span><b>مركز تعليم السنة</b><small>القاهرة · برامج ومجالس</small></div></div>`;
    if (data.visual === 'kids') return `<div class="preview-kids"><div class="kids-learning-board"><header><span>نتعلم السنة معًا</span><i class="bi bi-stars"></i></header><div class="kids-lessons"><article><i class="bi bi-book-half"></i><b>قصص نبوية</b></article><article><i class="bi bi-heart"></i><b>أخلاق جميلة</b></article><article><i class="bi bi-patch-check"></i><b>حديث اليوم</b></article></div><footer><span>محتوى آمن ومبسّط</span><b>ابدأ التعلّم <i class="bi bi-arrow-left"></i></b></footer></div><i class="kids-orbit-star star-one bi bi-star-fill"></i><i class="kids-orbit-star star-two bi bi-star-fill"></i></div>`;
    if (data.visual === 'competitions') return `<div class="preview-competition"><i class="bi bi-trophy-fill"></i><span>مسابقات السنة النبوية</span><strong>تعلّم · شارك · تميّز</strong><div><b>جوائز معرفية</b><small>تفاصيل المسابقة من المصدر</small></div></div>`;
    if (data.visual === 'exhibitions') return `<div class="preview-exhibition"><div class="exhibition-hall"><span class="exhibition-light light-one"></span><span class="exhibition-light light-two"></span><article><i class="bi bi-book"></i><small>السيرة</small></article><article class="main-exhibit"><span>رحمةٌ للعالمين</span><b>التعريف بالنبي ﷺ</b><small>محطات من السيرة والهدي</small></article><article><i class="bi bi-compass"></i><small>الهدي</small></article></div><div class="exhibition-caption"><span>معرض تعريفي</span><strong>تجربة معرفية بصرية</strong></div></div>`;
    return `<div class="preview-browser"><div class="preview-bar"><i></i><i></i><i></i></div><span class="preview-icon"><i class="bi ${data.icon}"></i></span><strong>${data.title}</strong><span>استكشف موارد القسم</span></div>`;
  };
  $$('.category-list [data-category]').forEach(button => button.addEventListener('click', () => {
    const data = categoryData[button.dataset.category];
    if (!data) return;
    $$('.category-list [data-category]').forEach(other => { other.classList.toggle('active', other === button); other.setAttribute('aria-selected', String(other === button)); });
    const preview = $('#categoryPreview');
    $('#categoryNumber').textContent = data.number;
    $('#categoryTitle').textContent = data.title;
    $('#categoryDescription').textContent = data.description;
    $('#categoryResources').innerHTML = data.resources.length ? data.resources.map(resource => `<span><img src="${resource[0]}" alt="${resource[1]}">${resource[1]}</span>`).join('') : '<span class="resource-note"><i class="bi bi-grid"></i> استعرض الموارد المسجلة في هذا القسم</span>';
    $('#categoryVisual').className = `category-visual visual-${data.visual}`;
    $('#categoryVisual').innerHTML = categoryVisualMarkup(data);
    $('#categoryCta').href = categoryByLegacyKey[button.dataset.category]?.to || '/search';
    preview.dispatchEvent(new CustomEvent('category:rendered'));
  }));

  const tabData = {
    apps: {
      eyebrow:'التطبيقات الهاتفية', title:'السنة بين يديك', description:'تطبيقات موثوقة تسهّل الوصول إلى الحديث وعلومه في كل وقت.', cta:'استكشف التطبيقات', visual:'phones',
      path:'/apps', items: [
        ['assets/img/512x512bb-112.jpg', 'الموسوعة الحديثية', 'تطبيق شامل لعلوم الحديث وعلومها','/apps/hadith-encyclopedia'],
        ['assets/img/download-83231.jpg', 'جامع الكتب التسعة', 'أحد أشهر التطبيقات للكتب التسعة','/apps/nine-books-collection'],
        ['assets/img/app-image-big-43027.jpg', 'الإمام المحدث الألباني', 'مكتبة شاملة لأعماله العلمية','/apps/al-albani-collection']
      ]
    },
    sites: {
      eyebrow:'مواقع خدمة السنة', title:'مصادر موثوقة على الويب', description:'مواقع متخصصة للبحث والتحقق والوصول إلى موسوعات السنة.', cta:'استكشف مواقع خدمة السنة', visual:'laptop',
      path:'/websites', items: [
        ['assets/img/512x512bb4.jpg', 'موسوعة الحديث', 'شبكة إسلام ويب','/websites/islamweb-hadith-encyclopedia'],
        ['assets/img/icon10.png', 'الموسوعة الشاملة', 'محرك بحثي في كتب السنة','/websites'],
        ['assets/img/unnamed9.jpg', 'موقع حديث', 'للتحقق من صحة الحديث','/websites']
      ]
    },
    papers: {
      eyebrow:'بحوث في السنة', title:'بحث ومراجعة ومعرفة', description:'بحوث ودراسات تعين الباحث على مراجعة قضايا السنة وعلوم الحديث.', cta:'استكشف البحوث', visual:'paper',
      path:'/research', items: [
        ['assets/img/hadith498.png', 'منهج المحدثين', 'بحوث ودراسات في نقد الروايات','/research/shamela-hadith-sciences'],
        ['assets/img/512x512bb4.jpg', 'دراسات السنة', 'أبحاث محكمة في علوم الحديث','/research'],
        ['assets/img/unnamed9.jpg', 'المكتبة البحثية', 'مراجع علمية للباحثين والطلاب','/research']
      ]
    }
  };

  const tabView = $('#tabView');
  const renderTab = key => {
    const data = tabData[key];
    if (!data || !tabView) return;
    tabView.classList.add('is-changing');
    const visual = data.visual === 'phones' ? `<div class="tool-visual phones-visual"><div class="phone-stack"><img src="assets/img/512x512bb-112.jpg" alt="الموسوعة الحديثية"><img src="assets/img/jamaa-alhdyth-alnboy-18.png" alt="جامع الحديث"></div></div>` : data.visual === 'laptop' ? `<div class="tool-visual laptop-visual"><div class="research-laptop"><div class="site-screen"><img src="assets/img/logo.png" alt="شعار موسوعة الحديث"><strong>موسوعة الحديث</strong><span>ابحث في مصادر السنة</span></div></div></div>` : `<div class="tool-visual paper-visual"><div class="research-paper"><h4>مراجعة البحث في علوم السنة</h4><div class="paper-lines"><i></i><i></i><i></i><i></i><i></i><i></i></div><span class="magnifier"></span><span class="research-pen"></span></div></div>`;
    setTimeout(() => {
      tabView.innerHTML = `<div class="tool-content"><span class="eyebrow">${data.eyebrow}</span><h3>${data.title}</h3><p>${data.description}</p><div class="tool-items">${data.items.map(item => `<a href="${item[3]}"><img src="${item[0]}" alt="${item[1]}"><span><b>${item[1]}</b><small>${item[2]}</small></span><i class="bi bi-arrow-up-left"></i></a>`).join('')}</div><a class="cta cta-gold tool-cta" href="${data.path}">${data.cta}</a></div>${visual}`;
      requestAnimationFrame(() => tabView.classList.remove('is-changing'));
    }, reducedMotion ? 0 : 260);
  };
  $$('.tabs [role="tab"]').forEach(tab => tab.addEventListener('click', () => {
    $$('.tabs [role="tab"]').forEach(other => other.setAttribute('aria-selected', String(other === tab)));
    renderTab(tab.dataset.tab);
  }));

  const centers = [
    {name:'مجالس سماع الحديث بالجامع الأزهر',country:'مصر',city:'القاهرة',type:'مجالس سماع السنة',coords:[30.045,31.262],description:'دروس ومجالس سماع الحديث بالجامع الأزهر.',path:'/centers/al-azhar-hadith-sessions'},
    {name:'المجالس الألفية في قراءة وسماع السنة النبوية',country:'مصر',city:'الإسكندرية',type:'مجالس سماع السنة',coords:[31.200,29.918],description:'مجالس لسماع وقراءة كتب السنة النبوية في مدينة الإسكندرية.'},
    {name:'الإدارة العامة لخدمة الكتاب والسنة',country:'السعودية',city:'مكة المكرمة',type:'هيئات ومراكز لنشر السنة',coords:[21.389,39.857],description:'جهة في رابطة العالم الإسلامي تقدم نشاطات في خدمة الكتاب والسنة.'},
    {name:'مجالس سماع الحديث بالرئاسة العامة لشؤون المسجد الحرام والمسجد النبوي',country:'السعودية',city:'مكة المكرمة',type:'مجالس سماع السنة',coords:[21.423,39.826],description:'حلقات تعليمية ومجالس سماع للمتون العلمية.'},
    {name:'مجالس سماع السنة بوزارة الشؤون الإسلامية والأوقاف',country:'الكويت',city:'مدينة الكويت',type:'مجالس سماع السنة',coords:[29.375,47.977],description:'مجالس سماع السنة من تنظيم وزارة الشؤون الإسلامية.'},
    {name:'مؤسسة الشارقة للقرآن الكريم والسنة النبوية',country:'الإمارات العربية المتحدة',city:'الشارقة',type:'هيئات ومراكز لنشر السنة',coords:[25.346,55.421],description:'مؤسسة حكومية تعنى بالقرآن الكريم والسنة النبوية.'},
    {name:'الهيئة العالمية للسنة النبوية',country:'النمسا',city:'فيينا',type:'هيئات ومراكز لنشر السنة',coords:[48.208,16.373],description:'هيئة إسلامية عالمية تنسق بين أنشطة الهيئات والمنظمات الإسلامية.'}
  ];
  if ($('#centersMap') && window.L && !window.__centersMap) {
    const map = L.map('centersMap',{scrollWheelZoom:false,worldCopyJump:true,zoomControl:true}).setView([30,36],3);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',{maxZoom:18,attribution:'&copy; OpenStreetMap'}).addTo(map);
    window.__centersMap = map;
    const markerLayer = L.layerGroup().addTo(map);
    const countries = [...new Set(centers.map(center => center.country))];
    $('#countryFilter').innerHTML += countries.map(country => `<option value="${country}">${country}</option>`).join('');
    const icon = selected => L.divIcon({className:'center-marker-wrap',html:`<span class="center-marker${selected ? ' selected' : ''}" aria-hidden="true"></span>`,iconSize:[selected ? 30 : 22,selected ? 30 : 22],iconAnchor:[selected ? 15 : 11,selected ? 15 : 11]});
    let selectedMarker = null;
    let selectedCenter = null;
    let firstDraw = true;
    const positionCenterCard = center => {
      const card = $('#centerInfo');
      const stage = $('.map-stage');
      if (!card || !stage || innerWidth < 992) return;
      const point = map.latLngToContainerPoint(center.coords);
      const cardWidth = 272;
      const panelSafeEdge = stage.clientWidth - 520;
      const left = Math.max(28,Math.min(point.x - cardWidth - 28,panelSafeEdge - cardWidth));
      const top = Math.max(110,Math.min(point.y + 28,stage.clientHeight - 190));
      card.style.left = `${left}px`;
      card.style.top = `${top}px`;
      card.style.right = 'auto';
    };
    const showCenter = (center, marker) => {
      if (selectedMarker) selectedMarker.setIcon(icon(false));
      marker.setIcon(icon(true)); selectedMarker = marker; selectedCenter = center;
      $('#centerCountry').textContent = `${center.city}، ${center.country} · ${center.type}`;
      $('#centerName').textContent = center.name; $('#centerDescription').textContent = center.description; $('#centerDetails').href = center.path || '/centers';
      $('#centerInfo').hidden = false;
      requestAnimationFrame(() => positionCenterCard(center));
    };
    const updateCount = value => {
      const count = $('#centerCount');
      const from = Number(count.textContent) || 0;
      if (window.gsap && !reducedMotion) {
        const state = {value:from};
        gsap.to(state,{value,duration:.65,ease:'power2.out',onUpdate:()=>{count.textContent=Math.round(state.value);}});
      } else count.textContent = value;
    };
    const drawMarkers = () => {
      markerLayer.clearLayers(); selectedMarker = null; selectedCenter = null; $('#centerInfo').hidden = true;
      const country = $('#countryFilter').value, type = $('#typeFilter').value;
      const filtered = centers.filter(center => (country === 'all' || center.country === country) && (type === 'all' || center.type === type));
      const entries = filtered.map(center => {
        const marker=L.marker(center.coords,{icon:icon(false),title:center.name}).addTo(markerLayer).bindTooltip(`<div class="center-tooltip"><strong>${center.name}</strong><span>${center.city}، ${center.country}</span></div>`,{direction:'top',offset:[0,-8],className:'centers-tooltip'});
        marker.on('click',()=>showCenter(center,marker));
        marker.on('add',()=>marker.getElement()?.setAttribute('aria-label',`${center.name}، ${center.city}، ${center.country}`));
        return {center,marker};
      });
      updateCount(filtered.length);
      if (filtered.length) {
        if (country === 'all' && type === 'all') map.flyTo([30,36],3,{duration:.8});
        else map.flyToBounds(filtered.map(center=>center.coords),{padding:[70,70],maxZoom:5,duration:.8});
      }
      if (firstDraw && entries.length) {
        const featured = entries.find(entry => entry.center.country === 'السعودية') || entries[0];
        setTimeout(()=>showCenter(featured.center,featured.marker),520);
        firstDraw = false;
      }
    };
    $('#countryFilter').addEventListener('change',drawMarkers); $('#typeFilter').addEventListener('change',drawMarkers);
    $('.center-info button').addEventListener('click',()=>{$('#centerInfo').hidden=true;if(selectedMarker)selectedMarker.setIcon(icon(false));selectedMarker=null;selectedCenter=null;});
    map.on('move zoom resize',()=>{if(selectedCenter&&!$('#centerInfo').hidden)positionCenterCard(selectedCenter);});
    addEventListener('resize',()=>{if(map._container?.isConnected){map.invalidateSize();if(selectedCenter)positionCenterCard(selectedCenter);}},{passive:true,once:true});
    drawMarkers();
  }

  if (!reducedMotion && matchMedia('(pointer:fine)').matches) {
    $$('.magnetic').forEach(button => {
      button.addEventListener('pointermove', event => {
        const box = button.getBoundingClientRect();
        const x = ((event.clientX - box.left) / box.width - .5) * 8;
        const y = ((event.clientY - box.top) / box.height - .5) * 8;
        button.style.transform = `translate(${x}px,${y}px)`;
      });
      button.addEventListener('pointerleave', () => button.style.transform = '');
    });
  }

  const initMotion = async () => {
    if (!gsap || !ScrollTrigger || !SplitText || !Flip) return;

    gsap.registerPlugin(ScrollTrigger, SplitText, Flip, Observer);
    document.documentElement.classList.add('gsap-ready');
    if (document.fonts?.ready) await document.fonts.ready;

    // A route transition can happen while fonts are still resolving. Stop here
    // instead of creating animations against a homepage that has unmounted.
    if (destroyed || !document.querySelector('.hero')) return;

    const motion = gsap.matchMedia();
    motionContext = motion;
    motion.add({
      reduce: '(prefers-reduced-motion: reduce)',
      desktop: '(min-width: 1200px)',
      tablet: '(min-width: 768px) and (max-width: 1199px)',
      mobile: '(max-width: 767px)',
      fine: '(pointer: fine)'
    }, context => {
      const { reduce, desktop, tablet, mobile, fine } = context.conditions;
      const allReveal = $$('.reveal, .reveal-stagger > *');

      if (reduce) {
        allReveal.forEach(element => element.classList.add('is-visible'));
        gsap.set('[data-motion], .reveal, .reveal-stagger > *', { clearProps: 'all' });
        return;
      }

      const heroHeadline = $('.hero h1');
      const headlineSplit = heroHeadline ? SplitText.create(heroHeadline, {
        type: 'lines',
        mask: 'lines',
        linesClass: 'hero-line++',
        aria: 'auto'
      }) : null;
      headlineSplit?.masks.forEach(mask => mask.classList.add('split-line-mask'));

      const heroIntro = gsap.timeline({ defaults: { ease: 'power4.out' } });
      heroIntro
        .from('.hero-kicker', { y: 18, autoAlpha: 0, duration: .55 })
        .from(headlineSplit?.lines || [], { yPercent: 110, autoAlpha: 0, duration: 1.05, stagger: .16 }, '-=.18')
        .from('.hero-copy > p:not(.hero-kicker)', { y: 20, autoAlpha: 0, duration: .7 }, '-=.5')
        .set('.hero-actions > *', { y: 0, autoAlpha: 1, clearProps: 'transform,opacity,visibility' }, '-=.42')
        .from('.browser-card', { x: -50, scale: .9, rotationY: 10, rotationX: 3, autoAlpha: 0, duration: 1.05 }, '-=.25')
        .from('.stats-card', { x: -28, y: 24, scale: .92, autoAlpha: 0, duration: .75 }, '-=.62')
        .from('.hero-search', { y: 14, scaleX: .92, autoAlpha: 0, duration: .72, transformOrigin: 'right center' }, '-=.18');

      gsap.to('.browser-card', { y: -7, duration: 4.2, repeat: -1, yoyo: true, ease: 'sine.inOut', delay: 2.6 });
      gsap.to('.stats-card', { y: 6, duration: 3.6, repeat: -1, yoyo: true, ease: 'sine.inOut', delay: 2.9 });

      if (fine && !tablet) {
        const scene = $('.product-scene');
        const panel = $('.browser-card');
        if (scene && panel) {
          const tiltY = gsap.quickTo(panel, 'rotationY', { duration: .55, ease: 'power3.out' });
          const tiltX = gsap.quickTo(panel, 'rotationX', { duration: .55, ease: 'power3.out' });
          scene.addEventListener('pointermove', event => {
            const bounds = scene.getBoundingClientRect();
            tiltY(4 + ((event.clientX - bounds.left) / bounds.width - .5) * 4);
            tiltX(1 - ((event.clientY - bounds.top) / bounds.height - .5) * 4);
          });
          scene.addEventListener('pointerleave', () => { tiltY(4); tiltX(1); });
        }
      }

      gsap.timeline({ scrollTrigger: { trigger: '.hero', start: 'top top', end: 'bottom top', scrub: 1 } })
        .to('.hero-copy', { y: -50, ease: 'none' }, 0)
        .to('.product-scene', { y: -90, ease: 'none' }, 0)
        .to('.hero-noise', { y: 30, ease: 'none' }, 0)
        .to('.hero-search', { y: -20, ease: 'none' }, 0);

      const searchShell = $('.hero-search-shell');
      searchShell?.addEventListener('focusin', () => gsap.to(searchShell, { scale: 1.01, duration: .25, ease: 'power2.out' }));
      searchShell?.addEventListener('focusout', () => gsap.to(searchShell, { scale: 1, duration: .25, ease: 'power2.out' }));

      $$('.section-title h2, .title-row h2').forEach(heading => {
        const split = SplitText.create(heading, { type: 'lines', mask: 'lines', aria: 'auto' });
        split.masks.forEach(mask => mask.classList.add('split-line-mask'));
        gsap.from(split.lines, { yPercent: 105, autoAlpha: 0, duration: .9, ease: 'power4.out', scrollTrigger: { trigger: heading, start: 'top 86%', once: true } });
      });

      const categorySection = $('.categories');
      const categoryPreview = $('#categoryPreview');
      if (categorySection && categoryPreview) gsap.context(() => {
        categoryPreview.addEventListener('category:rendered', () => {
          const visual = $('#categoryVisual');
          const copyItems = $$('.category-copy > *', categoryPreview);
          gsap.killTweensOf([visual, ...copyItems]);
          gsap.set([visual, ...copyItems], { autoAlpha: 1, clearProps: 'visibility' });
          gsap.fromTo(visual, { x: 22, scale: .975, autoAlpha: 0 }, { x: 0, scale: 1, autoAlpha: 1, duration: 1.15, ease: 'power2.inOut', overwrite: true, immediateRender: false, clearProps: 'transform,opacity,visibility' });
          gsap.fromTo(copyItems, { x: -20, autoAlpha: 0 }, { x: 0, autoAlpha: 1, duration: 1.08, stagger: .08, ease: 'power2.inOut', overwrite: true, immediateRender: false, clearProps: 'transform,opacity,visibility' });
        });
      }, categorySection);
      gsap.set('#categoryPreview, #categoryPreview .category-visual, #categoryPreview .category-copy > *', { autoAlpha: 1, clearProps: 'transform,opacity,visibility' });

      $$('.category-list [data-category]').forEach(button => {
        if (desktop) ScrollTrigger.create({
          trigger: button,
          start: 'top 52%',
          end: 'bottom 45%',
          onEnter: () => button.click(),
          onEnterBack: () => button.click()
        });
      });

      if (desktop) {
        const journeyStage = $('.journey-stage');
        const journeyScenes = $$('.journey-scene');
        const journeyDots = $$('.journey-progress i');
        let journeyIndex = 0;
        const showJourneyScene = (next, immediate = false) => {
          if (!journeyScenes[next] || next === journeyIndex && !immediate) return;
          const previous = journeyScenes[journeyIndex];
          const incoming = journeyScenes[next];
          gsap.killTweensOf(journeyScenes);
          journeyScenes.forEach((scene, index) => {
            if (index !== next && index !== journeyIndex) gsap.set(scene, { autoAlpha: 0, y: 0, scale: 1, zIndex: 0, visibility: 'hidden' });
          });
          journeyScenes.forEach((scene, index) => {
            scene.classList.toggle('is-active', index === next);
            scene.setAttribute('aria-hidden', String(index !== next));
          });
          journeyDots.forEach((dot, index) => dot.classList.toggle('active', index === next));
          if (immediate) {
            gsap.set(journeyScenes, { autoAlpha: 0, zIndex: 0, x: 0, scale: 1, clearProps: 'transform' });
            gsap.set(incoming, { autoAlpha: 1, zIndex: 2, x: 0, scale: 1 });
          } else {
            gsap.to(previous, { autoAlpha: .12, y: -28, scale: .99, zIndex: 1, duration: .82, ease: 'power2.inOut', overwrite: true });
            gsap.fromTo(incoming, { autoAlpha: 0, y: 52, scale: .982, zIndex: 2 }, { autoAlpha: 1, y: 0, scale: 1, duration: .96, ease: 'power2.inOut', overwrite: true, immediateRender: false });
          }
          journeyIndex = next;
        };
        showJourneyScene(0, true);
        ScrollTrigger.create({
          trigger: journeyStage,
          start: 'top 12%',
          end: '+=1040',
          pin: true,
          scrub: 1.45,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          onUpdate: self => showJourneyScene(Math.min(2, Math.floor(self.progress * 3)))
        });

        const track = $('.featured .resource-track');
        if (track) {
          const horizontal = gsap.to(track, {
            x: () => Math.max(0, track.scrollWidth - innerWidth + 150),
            ease: 'none',
            scrollTrigger: { trigger: '.featured .page-wrap', start: 'top 10%', end: () => `+=${Math.max(1500, track.scrollWidth * 1.65)}`, pin: true, scrub: 1.8, invalidateOnRefresh: true, anticipatePin: 1,
              onUpdate: () => $$('.resource-card', track).forEach(card => {
                const box = card.getBoundingClientRect();
                const distance = Math.abs(box.left + box.width / 2 - innerWidth / 2);
                gsap.set(card, { scale: gsap.utils.clamp(.975, 1, 1 - distance / innerWidth * .045), opacity: gsap.utils.clamp(.82, 1, 1 - distance / innerWidth * .22) });
              })
            }
          });
          horizontal.scrollTrigger?.refresh();
        }
      } else {
        gsap.set('.journey-scene', { autoAlpha: 1, y: 0, scale: 1, clearProps: 'transform,opacity,visibility' });
        if (mobile) gsap.set('.resource-card', { clearProps: 'transform,opacity,visibility' });
        else gsap.from('.resource-card', { x: 35, autoAlpha: 0, duration: .7, stagger: .08, ease: 'power3.out', scrollTrigger: { trigger: '.featured', start: 'top 82%', once: true } });
      }

      gsap.from('.centers .section-title', { y: 42, autoAlpha: 0, duration: 1.15, ease: 'power2.out', scrollTrigger: { trigger: '.centers', start: 'top 88%', once: true } });
      gsap.from('.map-copy', { x: 32, autoAlpha: 0, duration: 1.15, ease: 'power2.out', scrollTrigger: { trigger: '.map-panel', start: 'top 84%', once: true } });
      gsap.from('.map-stage', { clipPath: 'inset(0 6% 0 6% round 34px)', y: 35, scale: .99, autoAlpha: .5, duration: 1.35, ease: 'power3.out', scrollTrigger: { trigger: '.map-panel', start: 'top 84%', once: true } });
      ScrollTrigger.create({ trigger: '.map-stage', start: 'top 72%', once: true, onEnter: () => { window.__centersMap?.invalidateSize(); gsap.from('.center-marker-wrap', { scale: 0, autoAlpha: 0, stagger: .07, duration: .5, ease: 'back.out(1.7)' }); } });

      ['#countryFilter', '#typeFilter'].forEach(selector => $(selector)?.addEventListener('change', () => requestAnimationFrame(() => gsap.from('.center-marker', { scale: 0, autoAlpha: 0, stagger: .045, duration: .38, ease: 'power2.out' }))));
      const centerInfo = $('#centerInfo');
      if (centerInfo) new MutationObserver(() => {
        if (!centerInfo.hidden) gsap.fromTo(centerInfo, { x: 20, autoAlpha: 0 }, { x: 0, autoAlpha: 1, duration: .45, ease: 'power3.out' });
      }).observe(centerInfo, { attributes: true, attributeFilter: ['hidden'] });

      const tabs = $('.tabs');
      if (tabs) {
        const indicator = document.createElement('i');
        indicator.className = 'tab-motion-indicator';
        tabs.append(indicator);
        const placeIndicator = tab => {
          const tabsBox = tabs.getBoundingClientRect();
          const tabBox = tab.getBoundingClientRect();
          gsap.set(indicator, { left: tabBox.left - tabsBox.left + 10, width: tabBox.width - 20 });
        };
        const selected = $('.tabs [aria-selected="true"]');
        if (selected) placeIndicator(selected);
        $$('.tabs [role="tab"]').forEach(tab => tab.addEventListener('click', () => {
          const state = Flip.getState(indicator);
          placeIndicator(tab);
          Flip.from(state, { duration: .72, ease: 'power3.inOut', absolute: true });
          setTimeout(() => {
            const visual = $('.tool-visual', tabView);
            const content = $('.tool-content', tabView);
            if (visual) gsap.from(visual, { y: 28, rotationY: visual.classList.contains('laptop-visual') ? -7 : 0, scale: .94, autoAlpha: 0, duration: .78, ease: 'power3.inOut' });
            if (content) gsap.from(content.children, { x: -16, autoAlpha: 0, stagger: .055, duration: .5, ease: 'power3.out' });
            if (visual?.classList.contains('phones-visual')) gsap.from($$('.phone-stack img', visual), { y: index => index ? 60 : 35, rotation: index => index ? 6 : -4, autoAlpha: 0, stagger: .08, duration: .75, ease: 'power3.out' });
            if (visual?.classList.contains('paper-visual')) gsap.timeline().from($('.research-paper', visual), { y: 35, autoAlpha: 0, duration: .6 }).from($('.magnifier', visual), { x: 55, y: -20, duration: .75, ease: 'power2.inOut' }, '-=.2');
          }, reducedMotion ? 0 : 285);
        }));
      }

      gsap.from('.research-grid article', { y: 34, autoAlpha: 0, duration: .75, stagger: .12, ease: 'power3.out', scrollTrigger: { trigger: '.research-grid', start: 'top 82%', once: true } });
      $$('.research-img').forEach(image => gsap.from(image, { clipPath: 'inset(0 100% 0 0)', scale: 1.05, duration: .95, ease: 'power4.out', scrollTrigger: { trigger: image, start: 'top 86%', once: true } }));

      gsap.fromTo('.media-banner', { backgroundSize: '108%' }, { backgroundSize: '100%', ease: 'none', scrollTrigger: { trigger: '.media-section', start: 'top bottom', end: 'bottom top', scrub: 1 } });
      ScrollTrigger.create({ trigger: '.media-banner', start: 'top 75%', once: true, onEnter: () => {
        gsap.from('.media-banner > div:not(.media-art)', { x: -28, autoAlpha: 0, duration: .8, ease: 'power3.out' });
        gsap.from('.media-art', { x: 36, autoAlpha: 0, duration: .8, ease: 'power3.out' });
        gsap.fromTo('.media-banner .play', { scale: .86 }, { scale: 1, duration: .8, ease: 'power2.out', repeat: 1, yoyo: true });
      }});

      gsap.from('.contribute-visual img', { x: -25, y: 16, scale: 1.025, duration: 1.1, ease: 'power3.out', scrollTrigger: { trigger: '.contribution-inner', start: 'top 82%', once: true } });
      gsap.from('.contribution-copy > *', { y: 18, autoAlpha: 0, duration: .62, stagger: .08, ease: 'power3.out', scrollTrigger: { trigger: '.contribution-inner', start: 'top 82%', once: true } });
      gsap.from('.contribution-actions', { y: 18, duration: .72, ease: 'power3.out', clearProps:'transform', scrollTrigger: { trigger: '.contribution-inner', start: 'top 82%', once: true } });
      gsap.from('.footer-grid > *', { y: 26, autoAlpha: 0, duration: .72, stagger: .08, ease: 'power3.out', scrollTrigger: { trigger: '.site-footer', start: 'top 88%', once: true } });
    });

    addEventListener('load', () => ScrollTrigger.refresh(), { once: true });
  };

  initMotion().catch(() => revealTargets.forEach(target => target.classList.add('is-visible')));
  $('#year').textContent = new Date().getFullYear();

  return () => {
    destroyed = true;
    motionContext?.revert();
    motionContext = null;
  };
}
